// Category icons for the hazard buckets (F0b). Simple line glyphs in the Beacon paper
// language — 24×24, stroke: currentColor, fill: none, round caps/joins — so each icon
// inherits the row's tone and stays legible small. One conventional symbol per bucket;
// unknown buckets fall back to the generic "other" alert mark.
//
// Built with createElementNS (NOT innerHTML — innerHTML on an <svg> mis-namespaces its
// children in some engines, so they never render).

const NS = "http://www.w3.org/2000/svg";

interface Glyph {
  tag: "path" | "circle";
  attrs: Record<string, string>;
}

const GLYPHS: Record<string, Glyph[]> = {
  // flame
  fire: [{ tag: "path", attrs: { d: "M12 3c1.4 3 4 4.6 4 8a4 4 0 0 1-8 0c0-1.4.7-2.6 1.7-3.4C9.6 9.2 11 7.2 12 3Z" } }],
  // two wave crests
  flood: [
    { tag: "path", attrs: { d: "M3 13c2-2 4-2 6 0s4 2 6 0 4-2 6 0" } },
    { tag: "path", attrs: { d: "M3 18c2-2 4-2 6 0s4 2 6 0 4-2 6 0" } },
  ],
  // ground line with a seismograph spike
  earthquake: [{ tag: "path", attrs: { d: "M3 12h4l2-5 3 10 2-7 2 2h5" } }],
  // warning diamond (distinct from the triangle FAB) — "Danger"
  conflict: [
    { tag: "path", attrs: { d: "M12 3 21 12 12 21 3 12Z" } },
    { tag: "path", attrs: { d: "M12 8v5" } },
    { tag: "path", attrs: { d: "M12 16h.01" } },
  ],
  // generic alert circle
  other: [
    { tag: "circle", attrs: { cx: "12", cy: "12", r: "9" } },
    { tag: "path", attrs: { d: "M12 8v5" } },
    { tag: "path", attrs: { d: "M12 16h.01" } },
  ],
  // traffic cone with stripes
  traffic: [{ tag: "path", attrs: { d: "M8 20h8M10 20l2-13 2 13M10.5 13h3M11.2 9.5h1.6" } }],
  // cloud with a lightning bolt
  storm: [
    { tag: "path", attrs: { d: "M7.5 15a4 4 0 0 1-.6-8 5 5 0 0 1 9.4 1.4A3 3 0 0 1 16.5 14" } },
    { tag: "path", attrs: { d: "M12 12l-2 4h3l-2 4" } },
  ],
  // six-spoke snowflake
  snow: [{ tag: "path", attrs: { d: "M12 3v18M4.5 7.5l15 9M19.5 7.5l-15 9" } }],
  // ice crystal (single diamond, barred — distinct from the snowflake)
  ice: [
    { tag: "path", attrs: { d: "M12 3l5 9-5 9-5-9Z" } },
    { tag: "path", attrs: { d: "M7.5 12h9" } },
  ],
  // sun
  heat: [
    { tag: "circle", attrs: { cx: "12", cy: "12", r: "4" } },
    { tag: "path", attrs: { d: "M12 2v2M12 20v2M2 12h2M20 12h2M5 5l1.5 1.5M17.5 17.5 19 19M19 5l-1.5 1.5M6.5 17.5 5 19" } },
  ],
  // fog bands
  fog: [{ tag: "path", attrs: { d: "M4 9h16M4 13h16M6 17h12" } }],
  // hazmat flask
  hazmat: [
    { tag: "path", attrs: { d: "M10 3h4M10.5 3v4.6L6.5 16a2 2 0 0 0 1.9 2.9h7.2a2 2 0 0 0 1.9-2.9L13.5 7.6V3" } },
    { tag: "path", attrs: { d: "M8.6 13.5h6.8" } },
  ],
  // power symbol (IEC) — outage
  outage: [
    { tag: "path", attrs: { d: "M12 3v8" } },
    { tag: "path", attrs: { d: "M7.5 6.5a7 7 0 1 0 9 0" } },
  ],
};

/** Scattered-dots icon for the raw-reports layer toggle — individual observations.
 *  Filled dots (fill: currentColor) so they read on both the outline and filled-active button. */
export function rawReportsIcon(): SVGElement {
  const svg = document.createElementNS(NS, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("fill", "currentColor");
  svg.setAttribute("class", "raw-fab__icon");
  svg.setAttribute("aria-hidden", "true");
  for (const [cx, cy] of [[7, 8], [16, 7], [9, 16], [17, 15], [12, 11]] as const) {
    const c = document.createElementNS(NS, "circle");
    c.setAttribute("cx", String(cx));
    c.setAttribute("cy", String(cy));
    c.setAttribute("r", "1.8");
    svg.append(c);
  }
  return svg;
}

/** All hazard bucket keys (for pre-registering map marker images). */
export const HAZARD_KEYS = Object.keys(GLYPHS);

/** Standalone SVG markup string for a bucket in a fixed `color`, sized `size`px — for
 *  rasterising into a maplibre map image (markers can't render live DOM SVG). */
export function hazardIconMarkup(bucket: string, color: string, size: number): string {
  const inner = (GLYPHS[bucket] ?? GLYPHS.other)
    .map((g) => `<${g.tag} ${Object.entries(g.attrs).map(([k, v]) => `${k}="${v}"`).join(" ")}/>`)
    .join("");
  return `<svg xmlns="${NS}" viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="${color}" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">${inner}</svg>`;
}

/** SVG category icon for a hazard bucket, inheriting the surrounding text colour. */
export function hazardIcon(bucket: string): SVGElement {
  const svg = document.createElementNS(NS, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "2");
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");
  svg.setAttribute("class", "hazard-icon");
  svg.setAttribute("aria-hidden", "true");
  for (const g of GLYPHS[bucket] ?? GLYPHS.other) {
    const el = document.createElementNS(NS, g.tag);
    for (const [k, v] of Object.entries(g.attrs)) el.setAttribute(k, v);
    svg.append(el);
  }
  return svg;
}
